import { useCallback, useEffect, useRef, useState } from "react";

export interface Loaded<T> {
  data: T | null;
  error: string | null;
  reload: () => void;
}

/** A value fetched when the component mounts, with a way to fetch it again. */
export function useLoad<T>(load: () => Promise<T>): Loaded<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const loadRef = useRef(load);
  loadRef.current = load;

  const fetchNow = useCallback(() => {
    let isCurrent = true;
    loadRef.current().then(
      (value) => {
        if (isCurrent) {
          setData(value);
          setError(null);
        }
      },
      (failure: unknown) => {
        if (isCurrent) setError(failure instanceof Error ? failure.message : "Failed to load.");
      },
    );
    return () => {
      isCurrent = false;
    };
  }, []);

  useEffect(fetchNow, [fetchNow]);

  const reload = useCallback(() => void fetchNow(), [fetchNow]);
  return { data, error, reload };
}
